import React, { useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Col, Container, Row, Form, FormGroup, Input, Label } from "reactstrap";
import { Btn, H2, H6, P, Image } from "../../AbstractElements";
import { baseUrl, dynamicImage, mediaUrl } from "../../Utils";
import {
  CreateYourPassword,
  Done,
  EnterOtp,
  Href,
  NewPassword,
  ResetYourPassword,
  RetypePassword,
  Send,
  SignIn,
  Resend,
} from "../../Utils/Constants";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useConfig } from "../../Context/ConfigContext";

export default function ForgetPassword() {
  const { config } = useConfig();
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const navigate = useNavigate();
  const [timer, setTimer] = useState<number>(60);
  const [otpStartTime, setOtpStartTime] = useState<number | null>(null);

  const [formData, setFormData] = useState({
    email: "",
    otp: ["", "", "", "", "", ""],
    newPassword: "",
    retypePassword: "",
  });

  const otpInputRefs = useRef<HTMLInputElement[]>([]);

  const toggle = () => setPasswordVisible(!isPasswordVisible);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index?: number
  ) => {
    const { id, value } = e.target;

    if (index !== undefined) {
      if (/^[0-9]?$/.test(value)) {
        const newOtp = [...formData.otp];
        newOtp[index] = value;
        setFormData((prev) => ({ ...prev, otp: newOtp }));

        if (value && index < 5) {
          otpInputRefs.current[index + 1]?.focus();
        }
      }
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [id]: value,
      }));
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace") {
      if (formData.otp[index] === "" && index > 0) {
        otpInputRefs.current[index - 1]?.focus();
      }
    }
  };

  const sendOtp = async () => {
    try {
      setLoading(true);
      const res = await axios.post(`${baseUrl}send-otp`, {
        email: formData.email,
      });
      console.log(res, "res")
      toast.success(res.data.message);
      setOtpSent(true);
      setOtpVerified(false); // reset verification state
      setFormData((prev) => ({ ...prev, otp: ["", "", "", "", "", ""] }));

      // Start 60 second timer
      setOtpStartTime(Date.now());
      setTimer(60);
      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      setTimeout(() => {
        otpInputRefs.current[0]?.focus();
      }, 100);
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!otpStartTime || Date.now() - otpStartTime > 60000) {
      toast.error("OTP expired. Try again.");
      return;
    }

    try {
      setOtpVerifying(true);
      const otp = formData.otp.join("");
      const res = await axios.post(`${baseUrl}verify-otp`, {
        email: formData.email,
        otp,
      });
      toast.success(res.data.message);
      setOtpVerified(true);
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || "Invalid OTP");
      setOtpVerified(false);
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setResettingPassword(true);
      const otp = formData.otp.join("");
      const res = await axios.post(`${baseUrl}reset-password`, {
        email: formData.email,
        otp,
        password: formData.newPassword,
        password_confirmation: formData.retypePassword,
      });
      toast.success(res.data.message, {
        onClose: () => navigate("/login"),
        autoClose: 2000,
        pauseOnHover: true,
      });
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to reset password. Please try again.";
      toast.error(errorMessage);
    } finally {
      setResettingPassword(false);
    }
  };

  return (
    <div className="page-wrapper">
      <Container fluid className="p-0">
        <Row>
          <Col xs={12}>
            <div className="login-card login-dark">
              <div>
                <div className="text-center mb-4">
                  <Link className="logo" to={`${process.env.PUBLIC_URL}/dashboard`}>
                    <Image
                      className="img-fluid for-light m-auto"
                      src={config.logo ? `${mediaUrl}${config.logo}` : dynamicImage(`logo/logo1.webp`)}
                      alt="loginpage"
                      style={{ height: '50px' }}
                    />
                    <Image
                      className="img-fluid for-dark"
                      src={config.logo ? `${mediaUrl}${config.logo}` : dynamicImage(`logo/logo1.webp`)}
                      alt="loginpage"
                      style={{ height: '50px' }}
                    />
                  </Link>
                </div>
                <div className="login-main">
                  <Form className="theme-form" onSubmit={handleSubmit}>
                    <H2>{ResetYourPassword}</H2>

                    <FormGroup>
                      <Label className="pt-0">Enter your email</Label>
                      <Input
                        type="email"
                        id="email"
                        value={formData.email}
                        placeholder="Email"
                        onChange={handleInputChange}
                        required
                        disabled={otpSent || otpVerified} autoComplete="off"
                      />
                      <div className="text-end mt-2">
                        <Btn
                          color="primary"
                          block
                          className="m-t-10"
                          onClick={sendOtp}
                          type="button"
                          disabled={otpVerified}
                        >
                          {loading ? "Sending..." : Send}
                        </Btn>
                      </div>
                    </FormGroup>

                    {otpSent && !otpVerified && (
                      <div className="mt-4 mb-4 text-center">
                        <span className="reset-password-link">
                          {"Didn't receive OTP? "}
                          <a
                            className="btn-link text-danger"
                            href={Href}
                            onClick={(e) => {
                              e.preventDefault();
                              sendOtp();
                            }}
                          >
                            {loading ? "Sending..." : Resend}
                          </a>
                        </span>

                        {timer > 0 && (
                          <div className="text-danger mt-2">
                            OTP expires in {timer}s
                          </div>
                        )}
                        {timer === 0 && (
                          <div className="text-warning mt-2">
                            OTP expired. Please resend.
                          </div>
                        )}
                      </div>
                    )}

                    <FormGroup>
                      <Label className="pt-0">{EnterOtp}</Label>
                      <Row>
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <Col key={i} xs={2}>
                            <Input
                              className="text-center opt-text"
                              type="text"
                              maxLength={1}
                              value={formData.otp[i]}
                              onChange={(e) => handleInputChange(e, i)}
                              onKeyDown={(e) => handleKeyDown(e, i)}
                              disabled={!otpSent || otpVerified}
                              innerRef={(el) =>
                              (otpInputRefs.current[i] =
                                el as HTMLInputElement)
                              }
                              autoComplete="off"
                            />
                          </Col>
                        ))}
                      </Row>
                      <div className="text-end mt-2">
                        <Btn
                          color="primary"
                          className="m-t-10 w-100"
                          onClick={verifyOtp}
                          type="button"
                          block
                          disabled={!otpSent || otpVerifying || otpVerified}
                        >
                          {otpVerifying ? "Verifying..." : "Verify OTP"}
                        </Btn>
                      </div>
                    </FormGroup>

                    <H6 className="mt-4 f-w-700">{CreateYourPassword}</H6>

                    <FormGroup>
                      <Label>{NewPassword}</Label>
                      <div className="form-input position-relative">
                        <Input
                          type={isPasswordVisible ? "text" : "password"}
                          id="newPassword"
                          value={formData.newPassword}
                          onChange={handleInputChange}
                          required
                          placeholder="*********"
                          disabled={!otpVerified}
                          autoComplete="off"
                        />
                        <div className="show-hide" onClick={toggle}>
                          <span
                            className={!isPasswordVisible ? "show" : ""}
                          ></span>
                        </div>
                      </div>
                    </FormGroup>

                    <FormGroup>
                      <Label>{RetypePassword}</Label>
                      <div className="form-input position-relative">
                        <Input
                          type={isPasswordVisible ? "text" : "password"}
                          id="retypePassword"
                          value={formData.retypePassword}
                          onChange={handleInputChange}
                          required
                          placeholder="*********"
                          disabled={!otpVerified}
                          autoComplete="off"
                        />
                        <div className="show-hide" onClick={toggle}></div>
                      </div>
                    </FormGroup>

                    <Btn
                      type="submit"
                      color="primary"
                      className="w-100 mt-3"
                      block
                      disabled={resettingPassword || !otpVerified}
                    >
                      {resettingPassword ? "Resetting..." : Done}
                    </Btn>

                    <P className="mt-4 mb-0 text-center">
                      Already have an account?
                      <Link className="ms-2" to={`${process.env.PUBLIC_URL}/login`}>
                        {SignIn}
                      </Link>
                    </P>
                  </Form>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      <ToastContainer position="top-right" autoClose={1000} />
    </div>
  );
}
